/**
 * SmartTTClub Agent Configuration & Motivation Resolver
 * Manages the multi-agent roster: Pingwi, Pingflo, Pingpro, Pingmynd.
 */

export const AGENTS = {
  pingwi: {
    id: 'pingwi',
    name: 'Pingwi',
    archetype: 'The Spark',
    badgeKey: 'mhFlow_pingwi_badge',
    missionKey: 'mhFlow_pingwi_mission',
    storyKey: 'mhFlow_pingwi_story',
    primaryColor: '#FFA451',
    accentColor: '#FF8008',
    softTint: 'rgba(255, 164, 81, 0.12)',
    glowColor: 'rgba(255, 164, 81, 0.28)',
    themeClass: 'agent-theme-pingwi',
    emoji: '🐣',
    tags: ['Playful Joy', 'Motor Discovery', 'Confidence', 'Story Challenges'],
    tone: 'playful',
    defaultTagline: 'The spark of play. Love of the game before technique.',
    defaultBadge: 'Juniors & Discovery',
  },
  pingflo: {
    id: 'pingflo',
    name: 'Pingflo',
    archetype: 'The Flow State',
    badgeKey: 'mhFlow_pingflo_badge',
    missionKey: 'mhFlow_pingflo_mission',
    storyKey: 'mhFlow_pingflo_story',
    primaryColor: '#2A9BB5',
    accentColor: '#00D2FF',
    softTint: 'rgba(42, 155, 181, 0.12)',
    glowColor: 'rgba(42, 155, 181, 0.28)',
    themeClass: 'agent-theme-pingflo',
    emoji: '🌊',
    tags: ['Rhythm & Flow', 'Active Fitness', 'Casual Matches', 'Stress Relief'],
    tone: 'energetic',
    defaultTagline: 'The rhythm of progress. Feel the flow, enjoy getting fitter.',
    defaultBadge: 'Active Living & Flow',
  },
  pingpro: {
    id: 'pingpro',
    name: 'Pingpro',
    archetype: 'The Maestro',
    badgeKey: 'mhFlow_pingpro_badge',
    missionKey: 'mhFlow_pingpro_mission',
    storyKey: 'mhFlow_pingpro_story',
    primaryColor: '#1E40AF',
    secondaryColor: '#0B1F33',
    accentColor: '#3B82F6',
    softTint: 'rgba(30, 64, 175, 0.12)',
    glowColor: 'rgba(30, 64, 175, 0.3)',
    themeClass: 'agent-theme-pingpro',
    emoji: '⚡',
    tags: ['Tactical Rigor', 'Microcycles', 'Biomechanics', 'Rating Points'],
    tone: 'analytical',
    defaultTagline: 'The path to athletic mastery. Demanding, analytical, rigorous.',
    defaultBadge: 'Competition & Elite',
  },
  pingmynd: {
    id: 'pingmynd',
    name: 'Pingmynd',
    archetype: 'The Sage',
    badgeKey: 'mhFlow_pingmynd_badge',
    missionKey: 'mhFlow_pingmynd_mission',
    storyKey: 'mhFlow_pingmynd_story',
    primaryColor: '#0F7B6C',
    accentColor: '#34D399',
    softTint: 'rgba(15, 123, 108, 0.12)',
    glowColor: 'rgba(15, 123, 108, 0.28)',
    themeClass: 'agent-theme-pingmynd',
    emoji: '🌿',
    tags: ['Aerobic Chess', 'Neuroplasticity', 'Fall Prevention', 'Lifelong Health'],
    tone: 'wise',
    defaultTagline: 'The wisdom of movement. Cognitive vitality, balance, and safe health.',
    defaultBadge: 'Health & Longevity',
  },
};

export const AGENT_IDS = Object.keys(AGENTS);

/** Normalize legacy aliases */
export function normalizeAgentId(rawId) {
  const id = String(rawId || '').toLowerCase().trim();
  if (id === 'pingwe') return 'pingwi';
  if (id === 'pingflow') return 'pingflo';
  if (id === 'pingmind') return 'pingmynd';
  if (AGENTS[id]) return id;
  return null;
}

/** Calculate user age if birthdate available */
function getAgeFromBirthdate(birthdate) {
  if (!birthdate) return null;
  const birth = new Date(birthdate);
  if (isNaN(birth.getTime())) return null;
  const diffMs = Date.now() - birth.getTime();
  const ageDt = new Date(diffMs);
  return Math.abs(ageDt.getUTCFullYear() - 1970);
}

/**
 * Resolves which agent should accompany a player based on motivation, age, and profile settings.
 *
 * @param {Object} params
 * @param {Object} [params.user] User object
 * @param {Object} [params.smartProfile] SMART profile object
 * @param {string} [params.requestedAgent] Explicit agent override requested
 * @param {string} [params.homeRole] Current home role ('player', 'coach', 'admin')
 * @returns {typeof AGENTS['pingflo']} The matched agent config
 */
export function resolvePlayerAgent({ user, smartProfile, requestedAgent, homeRole } = {}) {
  // Staff / non-player home roles: honor an explicit agent pick, otherwise Pingflo.
  // Never route from the account's player purpose / technical SMART slots — a coach
  // who also plays must still be addressed as coach while that role is active.
  if (homeRole && homeRole !== 'player') {
    const normalizedRequested = normalizeAgentId(requestedAgent);
    if (normalizedRequested && AGENTS[normalizedRequested]) {
      return AGENTS[normalizedRequested];
    }
    return AGENTS.pingflo;
  }

  // 1. Explicit path flow in profile (player view only)
  const pathFlow = normalizeAgentId(
    smartProfile?.pathMeta?.flowId || smartProfile?.pathMeta?.agent_id || user?.flow_id
  );
  if (pathFlow && AGENTS[pathFlow]) {
    return AGENTS[pathFlow];
  }

  // 2. Age-based Junior override (ages 5–12 -> Pingwi)
  const age = getAgeFromBirthdate(user?.birthdate || user?.date_of_birth);
  if (age !== null && age <= 12) {
    return AGENTS.pingwi;
  }

  // 3. Purpose-based routing from profiling
  // Purpose 1 = Health / SCforH -> Pingmynd
  // Purpose 2 = Leisure / Recreation -> Pingflo (or Pingwi if junior)
  // Purpose 3 = Competitive League -> Pingpro
  const purposeId = Number(
    smartProfile?.slots?.S?.purposeId || user?.purpose_id || user?.user_purpose
  );

  if (purposeId === 1) {
    return AGENTS.pingmynd;
  }
  if (purposeId === 3) {
    return AGENTS.pingpro;
  }
  if (purposeId === 2) {
    return age !== null && age <= 14 ? AGENTS.pingwi : AGENTS.pingflo;
  }

  // 4. Focus attribute fallback
  const focus = String(smartProfile?.slots?.S?.focus || '').toLowerCase();
  if (
    focus.includes('santé') ||
    focus.includes('health') ||
    focus.includes('cognitive') ||
    focus.includes('hepa') ||
    focus.includes('neuro') ||
    focus.includes('chute') ||
    focus.includes('parkinson')
  ) {
    return AGENTS.pingmynd;
  }

  if (
    focus.includes('compétition') ||
    focus.includes('competition') ||
    focus.includes('league') ||
    focus.includes('classement') ||
    focus.includes('performance') ||
    focus.includes('tactique')
  ) {
    return AGENTS.pingpro;
  }

  if (focus.includes('junior') || focus.includes('découverte') || focus.includes('discovery') || focus.includes('jeu')) {
    return AGENTS.pingwi;
  }

  // 5. Default host agent
  return AGENTS.pingflo;
}
